import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Bell, Users, BarChart, Settings, LogOut, ExternalLink, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { supabase } from '../lib/supabase';
import TicketModal from '../components/TicketModal';
import Navigation from '../components/Navigation';

const mockTicket = {
  id: "1234",
  subject: "API Integration Issue",
  status: "In Progress",
  priority: "High",
  resolvedBy: "Ticc AI Assistant",
  service: "API Services",
  createdAt: "2025-03-27T10:30:00Z",
  resolvedAt: "2025-03-27T11:45:00Z",
  aiResponse: "Based on the error logs provided, I've identified that the API integration issue is caused by an outdated authentication token. I recommend refreshing the token and updating the API credentials in the integration settings.",
  customerQuery: "We're experiencing frequent timeouts with the API integration. The system shows connection refused errors every few minutes.",
  sentiment: "Urgent/Frustrated",
  category: "Technical/Integration",
  resolution: "Updated API authentication tokens and implemented automatic token refresh mechanism. Monitored the integration for 30 minutes with no further timeout issues."
};

const similarTickets = [
  {
    id: "1235",
    subject: "API Authentication Failed",
    priority: "Medium",
    status: "Resolved",
    similarity: "92%"
  },
  {
    id: "1236",
    subject: "API Token Expiration Issue",
    priority: "High",
    status: "Resolved",
    similarity: "89%"
  },
  {
    id: "1237",
    subject: "API Connection Timeout",
    priority: "Medium",
    status: "Resolved",
    similarity: "85%"
  }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [selectedTicket, setSelectedTicket] = useState<typeof mockTicket | null>(null);
  const [showSimilarTickets, setShowSimilarTickets] = useState(false);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#0a111e] flex">
      <div className="flex-1 overflow-auto">
        <header className="bg-[#0a111e] border-b border-white/[0.05] py-4 px-8">
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        </header>

        <main className="p-8">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#0F1824] rounded-xl p-6 border border-white/[0.05]">
              <h3 className="text-gray-400 text-sm font-medium">Open Tickets</h3>
              <p className="text-3xl font-bold text-white mt-2">24</p>
              <div className="text-green-500 text-sm mt-2">↑ 12% from last week</div>
            </div>
            <div className="bg-[#0F1824] rounded-xl p-6 border border-white/[0.05]">
              <h3 className="text-gray-400 text-sm font-medium">Response Time</h3>
              <p className="text-3xl font-bold text-white mt-2">1.2h</p>
              <div className="text-red-500 text-sm mt-2">↓ 8% from last week</div>
            </div>
            <div className="bg-[#0F1824] rounded-xl p-6 border border-white/[0.05]">
              <h3 className="text-gray-400 text-sm font-medium">AI Resolution Rate</h3>
              <p className="text-3xl font-bold text-white mt-2">76%</p>
              <div className="text-green-500 text-sm mt-2">↑ 5% from last week</div>
            </div>
          </div>

          {/* Recent Tickets */}
          <div className="mt-8">
            <h2 className="text-xl font-bold text-white mb-4">Recent Tickets</h2>
            <div className="bg-[#0F1824] rounded-xl border border-white/[0.05] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.05]">
                    <th className="text-left text-gray-400 text-sm font-medium p-4">ID</th>
                    <th className="text-left text-gray-400 text-sm font-medium p-4">Subject</th>
                    <th className="text-left text-gray-400 text-sm font-medium p-4">Priority</th>
                    <th className="text-left text-gray-400 text-sm font-medium p-4">Status</th>
                    <th className="text-left text-gray-400 text-sm font-medium p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={5} className="p-2 bg-pink-500/5 border-b border-white/[0.05]">
                      <div className="flex items-center gap-2 px-2 py-1">
                        <Layers className="h-4 w-4 text-pink-500" />
                        <span className="text-sm text-pink-500">
                          3 similar tickets found by AI
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-white/[0.05] bg-white/[0.02]">
                    <td className="text-white p-4">#1234</td>
                    <td className="text-white p-4">API Integration Issue</td>
                    <td className="p-4"><span className="px-2 py-1 bg-red-500/10 text-red-500 rounded-full text-sm">High</span></td>
                    <td className="p-4"><span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded-full text-sm">In Progress</span></td>
                    <td className="p-4">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setSelectedTicket(mockTicket)}
                          className="flex items-center gap-1 text-pink-500 hover:text-pink-400 transition-colors text-sm"
                        >
                          View Details
                          <ExternalLink className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowSimilarTickets(!showSimilarTickets)}
                          className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-sm"
                        >
                          Similar Tickets
                          {showSimilarTickets ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {showSimilarTickets && similarTickets.map((ticket) => (
                    <tr key={ticket.id} className="border-b border-white/[0.05] bg-white/[0.01]">
                      <td className="text-white p-4 pl-8"># {ticket.id}</td>
                      <td className="text-white p-4">
                        <div className="flex items-center gap-2">
                          <span>{ticket.subject}</span>
                          <span className="text-pink-500 text-sm">({ticket.similarity} match)</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-sm ${
                          ticket.priority === 'High' 
                            ? 'bg-red-500/10 text-red-500'
                            : 'bg-orange-500/10 text-orange-500'
                        }`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-green-500/10 text-green-500 rounded-full text-sm">
                          {ticket.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => setSelectedTicket({
                            ...mockTicket,
                            id: ticket.id,
                            subject: ticket.subject,
                            status: ticket.status,
                            priority: ticket.priority
                          })}
                          className="flex items-center gap-1 text-pink-500 hover:text-pink-400 transition-colors text-sm"
                        >
                          View Details
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="border-b border-white/[0.05]">
                    <td className="text-white p-4">#1233</td>
                    <td className="text-white p-4">Database Connection Error</td>
                    <td className="p-4"><span className="px-2 py-1 bg-orange-500/10 text-orange-500 rounded-full text-sm">Medium</span></td>
                    <td className="p-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 rounded-full text-sm">Resolved</span></td>
                    <td className="p-4">
                      <button
                        onClick={() => setSelectedTicket({
                          ...mockTicket,
                          id: "1233",
                          subject: "Database Connection Error",
                          priority: "Medium",
                          status: "Resolved"
                        })}
                        className="flex items-center gap-1 text-pink-500 hover:text-pink-400 transition-colors text-sm"
                      >
                        View Details
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      <Navigation onSignOut={handleSignOut} />

      {selectedTicket && (
        <TicketModal
          isOpen={!!selectedTicket}
          onClose={() => setSelectedTicket(null)}
          ticket={selectedTicket}
        />
      )}

      <div className="absolute inset-0 overflow-hidden -z-10">
        <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-pink-500/20 rounded-full blur-[180px] animate-pulse" />
        <div className="absolute -bottom-1/4 -right-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[180px] animate-pulse-slow" />
      </div>
    </div>
  );
};

export default Dashboard;